import React from 'react';
import Footer from '@/components/Footer';
import { Target, Users, Search, Workflow, Bot, Layout, Activity, ShieldCheck, LineChart, Lightbulb, Globe, Code, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

// Comprehensive service offerings data
const serviceCategories = [
  {
    id: 'strategic',
    title: 'Strategic Planning',
    description: 'Develop clear roadmaps for your business with our expert guidance and planning tools.',
    icon: Activity,
    color: 'border-goldcore',
    services: [
      {
        title: 'Business Strategy Development',
        description: 'Comprehensive strategic planning to align your business objectives with market opportunities.',
        features: ['Market positioning', 'Competitive analysis', 'Growth strategy', 'Business model optimization']
      },
      {
        title: 'Digital Transformation',
        description: 'Guide your business through technological change to improve operations and customer experience.',
        features: ['Technology assessment', 'Implementation roadmap', 'Change management', 'Digital capability building']
      },
      {
        title: 'Innovation Consulting',
        description: 'Develop new products, services, and business models to stay ahead of the competition.',
        features: ['Idea generation workshops', 'Product development', 'Innovation culture', 'Rapid prototyping']
      }
    ]
  },
  {
    id: 'market',
    title: 'Market Analysis',
    description: 'Gain insights into your market position and identify new opportunities for growth.',
    icon: LineChart,
    color: 'border-tacticalblue',
    services: [
      {
        title: 'Market Research',
        description: 'Comprehensive analysis of market trends, customer behavior, and competitive landscape.',
        features: ['Industry analysis', 'Customer segmentation', 'Competitor benchmarking', 'Market opportunity assessment']
      },
      {
        title: 'Customer Insights',
        description: 'Deep understanding of customer needs, preferences, and pain points to inform business decisions.',
        features: ['Customer journey mapping', 'Voice of customer research', 'Persona development', 'Sentiment analysis']
      },
      {
        title: 'Data Analytics',
        description: 'Turn your business data into actionable insights for informed decision-making.',
        features: ['Custom dashboards', 'Predictive analytics', 'Performance metrics', 'ROI analysis']
      }
    ]
  },
  {
    id: 'ai',
    title: 'AI Integration',
    description: 'Leverage cutting-edge AI technology to optimize your operations and decision-making.',
    icon: Bot,
    color: 'border-victorycoral',
    services: [
      {
        title: 'Custom AI Agents',
        description: 'Tailored AI solutions that enhance customer experiences and streamline operations.',
        features: ['Conversational agents', 'Customer service automation', 'Predictive assistants', '24/7 availability']
      },
      {
        title: 'Predictive Analytics',
        description: 'Harness the power of AI to forecast trends and make data-driven decisions.',
        features: ['Sales forecasting', 'Customer behavior prediction', 'Inventory optimization', 'Risk assessment']
      },
      {
        title: 'Process Automation',
        description: 'Use AI to automate repetitive tasks and free up resources for strategic initiatives.',
        features: ['Workflow automation', 'Document processing', 'Intelligent routing', 'Exception handling']
      }
    ]
  },
  {
    id: 'marketing',
    title: 'Digital Marketing',
    description: 'Amplify your online presence and connect with your target audience effectively.',
    icon: Target,
    color: 'border-goldcore',
    services: [
      {
        title: 'PPC Management',
        description: 'Data-driven paid campaigns that target high-intent customers with precision.',
        features: ['Google Ads optimization', 'Social media advertising', 'Retargeting campaigns', 'ROI tracking']
      },
      {
        title: 'SEO & Content Marketing',
        description: 'Improve your search rankings and establish authority through strategic content.',
        features: ['Technical SEO', 'Content strategy', 'Link building', 'Performance tracking']
      },
      {
        title: 'Social Media Management',
        description: 'Build community and drive engagement across social platforms.',
        features: ['Content calendar', 'Community management', 'Influencer partnerships', 'Social listening']
      }
    ]
  },
  {
    id: 'web',
    title: 'Web Development',
    description: 'Create impactful digital experiences that convert visitors into customers.',
    icon: Code,
    color: 'border-tacticalblue',
    services: [
      {
        title: 'Website Design & Development',
        description: 'Custom websites that reflect your brand and drive business results.',
        features: ['Responsive design', 'UX optimization', 'Performance tuning', 'SEO integration']
      },
      {
        title: 'E-commerce Solutions',
        description: 'Scalable online stores that provide seamless shopping experiences.',
        features: ['Payment integration', 'Inventory management', 'Customer accounts', 'Abandoned cart recovery']
      },
      {
        title: 'Web Applications',
        description: 'Custom web applications that streamline your business processes.',
        features: ['User authentication', 'Data visualization', 'API integration', 'Real-time updates']
      }
    ]
  },
  {
    id: 'pr',
    title: 'Public Relations',
    description: 'Build and maintain your brand reputation through effective communication strategies.',
    icon: Megaphone,
    color: 'border-victorycoral',
    services: [
      {
        title: 'Media Relations',
        description: 'Establish strong relationships with media outlets to enhance your brand visibility.',
        features: ['Press release development', 'Media outreach', 'Interview preparation', 'Coverage monitoring']
      },
      {
        title: 'Crisis Management',
        description: 'Protect your brand reputation during challenging situations with strategic communication.',
        features: ['Risk assessment', 'Response planning', 'Stakeholder communication', 'Reputation monitoring']
      },
      {
        title: 'Brand Messaging',
        description: 'Develop clear and consistent messaging that resonates with your target audience.',
        features: ['Message development', 'Brand voice guidelines', 'Storytelling strategies', 'Communication training']
      }
    ]
  }
];

const Solutions = () => {
  return (
    <div className="min-h-screen bg-softwhite">
      <div className="pt-32 pb-16 px-4 max-w-7xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-center">Our Business Solutions</h1>
        <p className="text-xl mb-16 text-center max-w-3xl mx-auto">Discover how our comprehensive suite of strategic services can help your business grow, adapt, and thrive in today's competitive landscape.</p>
        
        {/* Hero categories overview */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {serviceCategories.map(category => {
            const Icon = category.icon;
            return (
              <div key={category.id} className={`bg-white p-8 rounded-xl shadow-md border-t-4 ${category.color} hover:shadow-lg transition-all duration-300`}>
                <div className="flex items-center mb-4">
                  <div className={`p-3 rounded-full ${category.color === 'border-goldcore' ? 'bg-goldcore/10 text-goldcore' : category.color === 'border-tacticalblue' ? 'bg-tacticalblue/10 text-tacticalblue' : 'bg-victorycoral/10 text-victorycoral'}`}>
                    <Icon size={24} />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-4">{category.title}</h3>
                <p className="mb-6">{category.description}</p>
                <a href={`#${category.id}`} className="text-goldcore font-semibold hover:underline">Learn more →</a>
              </div>
            );
          })}
        </div>
        
        {/* Detailed service sections */}
        {serviceCategories.map(category => (
          <section id={category.id} key={`section-${category.id}`} className="mb-20 scroll-mt-20">
            <div className="bg-white rounded-xl shadow-md p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className={`p-3 rounded-full mr-4 ${category.color === 'border-goldcore' ? 'bg-goldcore/10 text-goldcore' : category.color === 'border-tacticalblue' ? 'bg-tacticalblue/10 text-tacticalblue' : 'bg-victorycoral/10 text-victorycoral'}`}>
                  <category.icon size={28} />
                </div>
                <h2 className="text-3xl font-bold">{category.title}</h2>
              </div>
              <p className="text-lg mb-8">{category.description}</p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.services.map((service, idx) => (
                  <div key={`${category.id}-service-${idx}`} className="border border-gray-200 rounded-lg p-6 hover:border-goldcore hover:shadow-md transition-all duration-300">
                    <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                    <p className="mb-4 text-gray-600">{service.description}</p>
                    <h4 className="font-semibold mb-2 text-goldcore">Key Features:</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      {service.features.map((feature, fidx) => (
                        <li key={`feature-${fidx}`}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ))}
        
        {/* CTA Section */}
        <div className="bg-knightblack text-softwhite rounded-xl p-8 md:p-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Business?</h2>
          <p className="text-lg mb-8 max-w-3xl mx-auto">Let our expert team help you navigate challenges, seize opportunities, and achieve sustainable growth.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="gold" asChild>
              <Link to="/chat">Chat with KnightAI</Link>
            </Button>
            <Button variant="dark" asChild>
              <Link to="/contact">Contact Our Team</Link>
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Solutions;
